<template>
  <div style="width: 100%" class="fill-height">
    <v-container>
      <v-row v-if="listView" class="justify-center">
        <v-col cols="12" sm="11" lg="11" :class="['px-0']">
          <!-- Sticky Header -->
          <div class="cat-sticky-header">
            <div class="controls-row">
              <v-text-field
                v-model="searchTerm"
                placeholder="Pretraži kategorije"
                dense
                outlined
                hide-details
                clearable
                class="filter-select controls-search"
                prepend-inner-icon="mdi-magnify"
                @input="onSearchInput"
                @click:clear="clearSearch"
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn class="add-cat-btn" elevation="0" @click="openAddCategoryForm">
                <v-icon small class="mr-1">mdi-plus</v-icon>
                Dodaj kategoriju
              </v-btn>
            </div>
            <div class="cat-thead">
              <div style="width:6%;"></div>
              <div class="cat-thead-cell" style="width:76%;">Naziv kategorije</div>
              <div class="cat-thead-cell" style="width:13%; text-align:center;">Stanje</div>
              <div style="width:5%;"></div>
            </div>
          </div>

          <!-- Rows -->
          <div class="cat-rows-body">
            <div
              v-for="(item, i) in currentCategories"
              :key="i"
              class="cat-row"
              :class="getRowClass(item.status)"
            >
              <div class="cat-row-inner">
                <div class="cat-cell-img" style="width:6%; padding-right:0;">
                  <v-img
                    v-if="item.picture"
                    :src="item.picture"
                    contain
                    height="44"
                    style="border-radius:6px; max-width:44px;"
                  ></v-img>
                  <div v-else style="width:44px; height:44px; border-radius:6px; background:#F1F5F9; margin-right:16px;"></div>
                </div>
                <div class="cat-cell" style="width:76%;">
                  <span class="cat-name one-liner-text">{{ $options.filters.capitalize(item.name) }}</span>
                </div>
                <div class="cat-cell" style="width:13%; justify-content:center;">
                  <v-chip x-small :class="getStatusChipClass(item.status)">
                    {{ showStatus(item.status) }}
                  </v-chip>
                </div>
                <div class="cat-cell-actions" style="width:5%;">
                  <v-menu offset-y left>
                    <template #activator="{ on, attrs }">
                      <v-btn icon small v-on="on" v-bind="attrs">
                        <v-icon size="18" color="#94A3B8">mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense class="cat-kebab-list">
                      <v-list-item
                        :disabled="item.status === 'PROCESSING'"
                        @click="openEditCategoryForm(item)"
                        class="cat-menu-item"
                      >
                        <v-icon small color="#3989C9" class="mr-2">mdi-pencil</v-icon>
                        <v-list-item-title class="cat-menu-title cat-menu-edit">Uredi</v-list-item-title>
                      </v-list-item>
                      <v-divider class="my-1"></v-divider>
                      <v-list-item @click="openCatDeleteDialog(item)" class="cat-menu-item">
                        <v-icon small color="#ef4444" class="mr-2">mdi-delete-outline</v-icon>
                        <v-list-item-title class="cat-menu-title cat-menu-delete">Obriši kategoriju</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row v-else class="justify-center">
        <v-col cols="12" sm="11" lg="11" :class="['px-0']">
          <!-- Grid view header -->
          <div class="cat-sticky-header">
            <div class="controls-row">
              <v-text-field
                v-model="searchTerm"
                placeholder="Pretraži kategorije"
                dense
                outlined
                hide-details
                clearable
                class="filter-select controls-search"
                prepend-inner-icon="mdi-magnify"
                @input="onSearchInput"
                @click:clear="clearSearch"
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn class="add-cat-btn" elevation="0" @click="openAddCategoryForm">
                <v-icon small class="mr-1">mdi-plus</v-icon>
                Dodaj kategoriju
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row
        v-if="!listView"
        class="d-flex justify-center align-center flex-wrap fill-height ma-0 pa-0"
      >
        <v-card
          class="category-grid-card ma-4"
          v-for="(item, i) in currentCategories"
          :key="i"
          hover
          height="300"
          width="250"
        >
          <div
            class="category-grid-header"
            :class="[
              item.status === 'OK'
                ? 'status-ok-header'
                : item.status === 'PROCESSING'
                  ? 'status-processing-header'
                  : 'status-failed-header'
            ]"
          >
            <v-img
              height="190"
              width="250"
              v-if="item.picture"
              :src="item.picture"
              class="category-grid-image"
            >
            </v-img>
            <div v-else class="no-image-placeholder">
              <v-icon size="64" color="rgba(57, 137, 201, 0.3)">mdi-image-off</v-icon>
            </div>
          </div>
          <div class="category-grid-content">
            <h3 class="category-grid-title one-liner-text">
              {{ $options.filters.capitalize(item.name) }}
            </h3>
            <p
              class="category-grid-status one-liner-text"
              :class="[
                item.status === 'OK'
                  ? 'status-text-ok'
                  : item.status === 'PROCESSING'
                    ? 'status-text-processing'
                    : 'status-text-failed'
              ]"
            >
              Status: {{ showStatus(item.status) }}
            </p>
          </div>
          <v-divider></v-divider>
          <v-card-actions class="category-grid-actions">
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  :disabled="item.status === 'PROCESSING'"
                  icon
                  color="#3989C9"
                  @click="openEditCategoryForm(item)"
                  @keypress.enter="openEditCategoryForm(item)"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Uredi kategoriju</span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-row>
    </v-container>
    <add-category ref="addCategory" @add="refreshCategory"></add-category>
    <edit-category ref="editCategory" @edit="refreshCategory"></edit-category>

    <v-dialog v-model="catDeleteDialog" max-width="520" @click:outside="catDeleteDialog = false">
      <v-card class="del-modal-card" style="height: 480px; display: flex; flex-direction: column;">
        <div class="del-modal-header">
          <div class="del-modal-header-gloss"></div>
          <div class="del-modal-header-content">
            <v-icon color="white" size="22" class="mr-2">mdi-delete-outline</v-icon>
            <div>
              <div class="del-modal-title">Brisanje kategorije</div>
              <div v-if="catItemToDelete" class="del-modal-subtitle">{{ catItemToDelete.name }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-btn icon small @click="catDeleteDialog = false" class="del-modal-close-btn">
              <v-icon color="white" size="20">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text class="pt-5 pb-3 px-6" style="flex: 1; overflow-y: auto; min-height: 0;">
          <div v-if="catDeleteLoading" class="d-flex align-center justify-center py-8">
            <v-progress-circular indeterminate color="#3989C9" size="38" width="3"></v-progress-circular>
            <span class="ml-4 body-2" style="color:#64748b;">Provjera cjenika...</span>
          </div>

          <template v-else-if="catDeleteErrorMsg === 'on_pricelist'">
            <div class="del-info-box del-info-box--warn mb-4">
              <v-icon size="20" color="#b45309" class="mr-3 flex-shrink-0">mdi-tag-outline</v-icon>
              <span>Kategorija ima artikle na cjeniku. Promijenite im kategoriju na cjeniku.</span>
            </div>
            <div class="del-table-label">ARTIKLI NA CJENIKU</div>
            <div class="del-table">
              <div v-for="item in catPricelistItems" :key="item.id" class="del-table-row">
                <v-icon size="16" color="#94a3b8" class="mr-2">mdi-tag-outline</v-icon>
                <span>{{ item.name }}</span>
                <span v-if="item.location" class="ml-auto caption" style="color:#94a3b8;">{{ item.location }}</span>
              </div>
            </div>
          </template>

          <div v-else-if="catDeleteErrorMsg === 'error'" class="del-info-box del-info-box--error">
            <v-icon size="20" color="#b91c1c" class="mr-3">mdi-alert-circle-outline</v-icon>
            <span>Došlo je do greške. Pokušajte ponovo.</span>
          </div>

          <template v-else>
            <div class="del-info-box del-info-box--confirm">
              <v-icon size="20" color="#b91c1c" class="mr-3 flex-shrink-0">mdi-alert-remove-outline</v-icon>
              <span>Kategorija će biti trajno obrisana. Artikli ostaju u sustavu, samo se uklanja kategorija.</span>
            </div>
          </template>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pb-5 px-6" style="flex-shrink: 0;">
          <v-spacer></v-spacer>
          <v-btn text class="del-btn-cancel" @click="catDeleteDialog = false">Zatvori</v-btn>
          <v-btn
            v-if="!catDeleteErrorMsg"
            elevation="0"
            class="del-btn-delete"
            :loading="catDeleteLoading"
            :disabled="catDeleteLoading"
            @click="confirmCatDelete"
          >
            <v-icon small class="mr-2">mdi-delete-outline</v-icon>
            Obriši kategoriju
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { df } from '@/plugins/firebase'
import { clone } from '@/plugins/utils'
import state from '@/state'
import flow from '@/mixins/flow'
import AddCategory from '@/modules/company/components/AddCategory'
import EditCategory from '@/modules/company/components/EditCategory'
import applicationSettings from '@/mixins/applicationSettings' // don't move.. detaches firebase listeners
import EventBus from '@/plugins/event-bus'
import { collection, query, where, getDocs, doc, updateDoc, deleteField, limit } from 'firebase/firestore'

export default {
  inject: ['showLoader', 'hideLoader'],
  mixins: [flow, applicationSettings],
  components: { AddCategory, EditCategory },
  data: () => ({
    loading: false,
    listeners: [],
    categories: [],
    currentCategories: [],
    searchEmitDestination: 'categoriesSearch',
    searchTerm: '',
    searchTimeout: null,
    catDeleteDialog: false,
    catDeleteLoading: false,
    catDeleteErrorMsg: '',
    catItemToDelete: null,
    catPricelistItems: []
  }),
  mounted () {
    const listener = df.doc(`company_categories/${state.getCurrentCompany().id}`).onSnapshot(doc => {
      if (doc && doc.data() && doc.data().categories && Object.keys(doc.data().categories).length > 0) {
        this.categories = Object.keys(doc.data().categories).map(key => {
          return { ...doc.data().categories[key], [`hover-${doc.data().categories[key].id}`]: false }
        })
        this.categories.sort((a, b) => a.name > b.name ? 1 : -1)
      }
    })

    EventBus.$on(this.searchEmitDestination, (searchObject) => {
      this.searchAll(searchObject.searchString)
    })

    this.listeners.push(listener)
  },
  computed: {
    listView () {
      return state.isListView()
    }
  },
  watch: {
    categories: {
      deep: true,
      handler (nv, ov) {
        if (nv !== ov) {
          let currentCategories = []
          if (this.filter && this.filter !== '') {
            currentCategories = nv.filter(category => category.name.toLowerCase().includes(this.filter.toLowerCase()))
          } else {
            currentCategories = nv
          }
          this.currentCategories = clone(currentCategories.sort((a, b) => a.name.localeCompare(b.name)))
        }
      }
    }
  },
  methods: {
    onSearchInput () {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applySearch()
      }, 500)
    },
    applySearch () {
      if (!this.searchTerm || this.searchTerm.trim().length === 0) {
        this.currentCategories = this.categories
      } else {
        this.currentCategories = this.categories.filter(category =>
          category.name.toLowerCase().includes(this.searchTerm.toLowerCase())
        )
      }
    },
    clearSearch () {
      this.searchTerm = ''
      this.currentCategories = this.categories
    },
    searchAll (val) {
      if (!val) {
        this.currentCategories = this.categories
      } else {
        this.currentCategories = this.categories.filter(category => category.name.toLowerCase().includes(val.toLowerCase()))
      }
    },
    openAddCategoryForm () {
      this.$refs.addCategory.open()
    },
    openEditCategoryForm (item) {
      this.$refs.editCategory.open(item)
    },
    hoverOver (ref, item) {
      const container = this.$refs[ref]
      if (!container || !container[0]) return
      if (container[0].associateHeight < container[0].scrollHeight) {
        item[ref] = true
      }
    },
    showStatus (status) {
      switch (status) {
        case 'OK':
          return 'Aktivna'
        case 'PROCESSING':
          return 'Procesira se'
        case 'FAILED':
          return 'Greška'
        default:
          return status
      }
    },
    getRowClass (status) {
      if (status === 'OK') return 'status-ok'
      if (status === 'PROCESSING') return 'status-warn'
      return 'status-failed'
    },
    getStatusChipClass (status) {
      if (status === 'OK') return 'status-chip-ok'
      if (status === 'PROCESSING') return 'status-chip-warn'
      return 'status-chip-failed'
    },
    async openCatDeleteDialog (category) {
      this.catItemToDelete = category
      this.catDeleteErrorMsg = ''
      this.catPricelistItems = []
      this.catDeleteLoading = true
      this.catDeleteDialog = true

      try {
        const companyId = state.getCurrentCompany().id
        const categoryId = category.id

        // Dohvati sve lokacije za firmu
        const locsSnap = await getDocs(
          query(collection(df, 'locations'), where('company_id', '==', companyId))
        )

        // Za svaku lokaciju provjeri postoje li artikli ove kategorije na cjeniku
        const found = []
        await Promise.all(
          locsSnap.docs.map(async locDoc => {
            const locName = locDoc.data().name || locDoc.id
            const itemsSnap = await getDocs(
              query(
                collection(df, `location_pricelist/${locDoc.id}/items`),
                where('category_ids', 'array-contains', categoryId),
                limit(20)
              )
            )
            itemsSnap.docs.forEach(d => {
              const data = d.data()
              // Ignoriraj stock-only dokumente
              if (data && (data.name || data.prices)) {
                found.push({ id: d.id, name: data.name || d.id, location: locName })
              }
            })
          })
        )

        if (found.length > 0) {
          this.catPricelistItems = found
          this.catDeleteErrorMsg = 'on_pricelist'
        }
      } catch (e) {
        console.error('Greška pri provjeri kategorije:', e)
        this.catDeleteErrorMsg = 'error'
      } finally {
        this.catDeleteLoading = false
      }
    },

    async confirmCatDelete () {
      if (!this.catItemToDelete || this.catDeleteErrorMsg) return
      this.catDeleteLoading = true
      try {
        const companyId = state.getCurrentCompany().id
        const categoryId = this.catItemToDelete.id
        await updateDoc(
          doc(df, `company_categories/${companyId}`),
          { [`categories.${categoryId}`]: deleteField() }
        )
        this.currentCategories = this.currentCategories.filter(c => c.id !== categoryId)
        this.categories = this.categories.filter(c => c.id !== categoryId)
        this.catDeleteDialog = false
        this.catItemToDelete = null
      } catch (e) {
        console.error('Greška pri brisanju kategorije:', e)
        this.catDeleteErrorMsg = 'error'
      } finally {
        this.catDeleteLoading = false
      }
    },

    refreshCategory (item) {
      if (item.action === 'ADD') {
        this.categories.unshift(
          { ...item, ...{ status: 'PROCESSING' } }
        )
        this.filter = item.name
      } else if (item.action === 'EDIT') {
        this.categories = this.categories.map(_item => {
          if (_item.id === item.id) {
            _item.name = item.name
            _item.status = 'PROCESSING'
          }
          return _item
        })
        this.filter = item.name
      }
    }
  }
}
</script>
<style scoped>
/* ── Sticky Header ─────────────────────────────────────────── */
.cat-sticky-header {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 14px 0 0;
  border-left: 3px solid #00BCD4;
  border-bottom: 1px solid #EEF2F5;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.controls-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px 16px;
}

.controls-search {
  flex: 1;
  max-width: 320px;
}

.controls-search >>> .v-input__control,
.controls-search >>> .v-input__slot {
  height: 40px !important;
  min-height: 40px !important;
}

.filter-select >>> fieldset {
  border-color: #B0BEC5 !important;
  border-width: 1.5px !important;
  border-radius: 8px !important;
}

.filter-select:hover >>> fieldset {
  border-color: #00BCD4 !important;
}

.filter-select.v-input--is-focused >>> fieldset {
  border-color: #00BCD4 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: #ffffff !important;
}

.add-cat-btn {
  background: rgba(0, 188, 212, 0.08) !important;
  color: #00838F !important;
  border: 1.2px solid #00b3be !important;
  border-radius: 8px !important;
  height: 40px !important;
  min-height: 40px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  padding: 0 16px !important;
}

/* ── Table Head ────────────────────────────────────────────── */
.cat-thead {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #EEF2F5;
}

.cat-thead-cell {
  font-size: 11px;
  font-weight: 600;
  color: #94A3B8;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 0 8px;
}

/* ── Rows Container ────────────────────────────────────────── */
.cat-rows-body {
  background: #F5F7FA;
  padding: 6px 0 2px;
}

/* ── Data Rows ─────────────────────────────────────────────── */
.cat-row {
  display: flex;
  align-items: stretch;
  border: 1px solid #EEF2F5;
  border-left: 3px solid #E0E0E0;
  border-radius: 6px;
  background: #ffffff;
  transition: border-left-color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  min-height: 56px;
  margin-bottom: 4px;
  overflow: hidden;
}

.cat-row:hover {
  border-color: #00BCD4 !important;
  background: #F8FAFB !important;
}

.cat-row.status-ok     { border-left-color: #4ADE80 !important; }
.cat-row.status-warn   { border-left-color: #FBBF24 !important; }
.cat-row.status-failed { border-left-color: #F87171 !important; }

.cat-row-inner {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 56px;
}

.cat-cell-img {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  flex-shrink: 0;
}

.cat-cell {
  display: flex;
  align-items: center;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
  padding: 0 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.cat-cell-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  flex-shrink: 0;
}

.cat-name {
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Status Chips ──────────────────────────────────────────── */
.status-chip-ok {
  background: #DCFCE7 !important;
  color: #166534 !important;
  font-weight: 600 !important;
}

.status-chip-warn {
  background: #FEF9C3 !important;
  color: #854D0E !important;
  font-weight: 600 !important;
}

.status-chip-failed {
  background: #FEE2E2 !important;
  color: #991B1B !important;
  font-weight: 600 !important;
}

/* ── Kebab Menu ────────────────────────────────────────────── */
.cat-kebab-list {
  padding: 4px 0 !important;
  min-width: 140px !important;
}

.cat-menu-item {
  min-height: 36px !important;
  padding: 0 12px !important;
}

.cat-menu-item >>> .v-list-item__content {
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
}

.cat-menu-title {
  font-size: 13px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
}

.cat-menu-edit   { color: #3989C9 !important; }
.cat-menu-delete { color: #ef4444 !important; }

.cat-menu-item.v-list-item--disabled .cat-menu-edit,
.cat-menu-item.v-list-item--disabled .v-icon {
  opacity: 0.4 !important;
}

/* ── Delete modal ──────────────────────────────────────────── */
.del-modal-card { border-radius: 16px !important; overflow: hidden !important; border: 1px solid rgba(0,179,190,0.15) !important; box-shadow: 0 8px 32px rgba(0,0,0,0.14) !important; }
.del-modal-header { position: relative; padding: 20px 24px; background: linear-gradient(135deg, rgba(0,179,190,0.55) 0%, rgba(57,137,201,0.5) 50%, rgba(97,84,164,0.55) 100%); overflow: hidden; }
.del-modal-header-gloss { position: absolute; top: 0; left: -100%; width: 60%; height: 100%; background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%); pointer-events: none; }
.del-modal-header-content { position: relative; z-index: 1; display: flex; align-items: center; }
.del-modal-title   { color: white !important; font-weight: 700 !important; font-size: 16px !important; letter-spacing: 0.3px; text-shadow: 0 1px 4px rgba(0,0,0,0.15); line-height: 1.3; }
.del-modal-subtitle { color: rgba(255,255,255,0.8) !important; font-size: 12px !important; font-weight: 500 !important; margin-top: 2px; }
.del-modal-close-btn { background: rgba(255,255,255,0.15) !important; }
.del-modal-close-btn:hover { background: rgba(255,255,255,0.25) !important; }
.del-info-box { display: flex; align-items: flex-start; padding: 14px 16px; border-radius: 10px; font-size: 14px; line-height: 1.5; }
.del-info-box--warn    { background: #fffbeb; border: 1px solid #fde68a; color: #78350f; }
.del-info-box--error   { background: #fff1f2; border: 1px solid #fecdd3; color: #7f1d1d; }
.del-info-box--confirm { background: #fff1f2; border: 1px solid #fecdd3; color: #7f1d1d; }
.del-table-label { font-size: 11px; font-weight: 700; color: #3989C9; letter-spacing: 0.6px; margin-bottom: 6px; padding-left: 2px; }
.del-table { border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
.del-table-row { display: flex; align-items: center; padding: 10px 14px; font-size: 14px; color: #334155; border-bottom: 1px solid #f1f5f9; }
.del-table-row:last-child { border-bottom: none; }
.del-table-row:nth-child(even) { background: #f8fafc; }
.del-btn-cancel { color: #3989C9 !important; font-weight: 600 !important; font-size: 14px !important; text-transform: none !important; border-radius: 10px !important; height: 42px !important; padding: 0 18px !important; }
.del-btn-delete { background: linear-gradient(135deg, #f43f5e 0%, #dc2626 100%) !important; color: white !important; border-radius: 10px !important; height: 42px !important; padding: 0 22px !important; font-weight: 600 !important; font-size: 14px !important; text-transform: none !important; box-shadow: 0 4px 12px rgba(220,38,38,0.3) !important; }

/* Grid View - Category Cards */
.category-grid-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.1) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  overflow: hidden;
}

.category-grid-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 12px 48px rgba(0, 179, 190, 0.2) !important;
  border-color: rgba(0, 179, 190, 0.4) !important;
}

.category-grid-header {
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Status Headers - Grid View */
.status-ok-header {
  background: linear-gradient(135deg,
    rgba(76, 175, 80, 0.5) 0%,
    rgba(56, 142, 60, 0.45) 50%,
    rgba(76, 175, 80, 0.5) 100%) !important;
}

.category-grid-card:hover .status-ok-header {
  background: linear-gradient(135deg,
    rgba(76, 175, 80, 0.65) 0%,
    rgba(56, 142, 60, 0.6) 50%,
    rgba(76, 175, 80, 0.65) 100%) !important;
}

.status-processing-header {
  background: linear-gradient(135deg,
    rgba(255, 152, 0, 0.5) 0%,
    rgba(255, 193, 7, 0.45) 50%,
    rgba(255, 152, 0, 0.5) 100%) !important;
}

.category-grid-card:hover .status-processing-header {
  background: linear-gradient(135deg,
    rgba(255, 152, 0, 0.65) 0%,
    rgba(255, 193, 7, 0.6) 50%,
    rgba(255, 152, 0, 0.65) 100%) !important;
}

.status-failed-header {
  background: linear-gradient(135deg,
    rgba(244, 67, 54, 0.5) 0%,
    rgba(211, 47, 47, 0.45) 50%,
    rgba(244, 67, 54, 0.5) 100%) !important;
}

.category-grid-card:hover .status-failed-header {
  background: linear-gradient(135deg,
    rgba(244, 67, 54, 0.65) 0%,
    rgba(211, 47, 47, 0.6) 50%,
    rgba(244, 67, 54, 0.65) 100%) !important;
}

.category-grid-image {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.category-grid-card:hover .category-grid-image {
  transform: scale(1.05);
}

/* No Image Placeholder */
.no-image-placeholder {
  height: 190px;
  width: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(245, 245, 245, 0.5);
}

.category-grid-content {
  padding: 16px;
}

.category-grid-title {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  margin-bottom: 4px !important;
  letter-spacing: 0.3px !important;
}

.category-grid-status {
  font-size: 15px !important;
  font-weight: 500 !important;
  margin-bottom: 4px !important;
}

.category-grid-actions {
  padding: 8px 16px !important;
}

/* Font Weight */
.font-weight-medium {
  font-weight: 500 !important;
}

/* One Liner Text */
.one-liner-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
